/**
 * Price for a colour/type card in a variant-level collection.
 *
 * Shopify only returns the collection's own variants to Liquid, so the card can't see
 * every size of its option value (e.g. all Loose Leaf pouches). Once the card is near
 * the viewport this loads the full product from /products/{handle}.js and shows the
 * lowest price of all variants with the card's option value, available ones first,
 * prefixed with "From" when their prices differ (unless data-hide-from is "true").
 */
if (!customElements.get('variant-group-price')) {
  const productRequests = new Map();

  const fetchProduct = (handle) => {
    if (!productRequests.has(handle)) {
      const root = window.Shopify?.routes?.root || '/';
      const request = fetch(`${root}products/${encodeURIComponent(handle)}.js`, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`${response.status} ${handle}`);
          return response.json();
        })
        .catch((error) => {
          productRequests.delete(handle);
          throw error;
        });
      productRequests.set(handle, request);
    }
    return productRequests.get(handle);
  };

  const formatMoney = (cents) => `<price-money><bdi>${theme.Currency.formatMoneyDisplay(cents)}</bdi></price-money>`;

  class VariantGroupPrice extends HTMLElement {
    connectedCallback() {
      if (this.dataset.loaded) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.update();
        },
        { rootMargin: '400px 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
    }

    async update() {
      let product;
      try {
        product = await fetchProduct(this.dataset.handle);
      } catch (error) {
        console.warn('variant-group-price:', error);
        return;
      }

      const optionIndex = Number(this.dataset.optionIndex);
      const group = product.variants.filter((variant) => variant.options[optionIndex] === this.dataset.optionValue);
      if (!group.length) return;

      const available = group.filter((variant) => variant.available);
      const cheapest = (available.length ? available : group).reduce((min, variant) =>
        variant.price < min.price ? variant : min
      );
      const prices = group.map((variant) => variant.price);
      const showFrom = this.dataset.hideFrom !== 'true' && Math.min(...prices) !== Math.max(...prices);

      this.render(cheapest, showFrom);
      this.dataset.loaded = 'true';
    }

    render(variant, showFrom) {
      const price = this.querySelector('.price');
      if (!price) return;

      const money = formatMoney(variant.price);
      const html = showFrom ? this.dataset.fromTemplate.replace('__PRICE__', money) : money;
      price.querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale').forEach((item) => {
        item.innerHTML = html;
      });

      const onSale = variant.available && variant.compare_at_price > variant.price;
      price.classList.toggle('price--on-sale', onSale);
      const compare = price.querySelector('.price__compare .price-item--regular');
      if (onSale && compare) compare.innerHTML = formatMoney(variant.compare_at_price);
    }
  }

  customElements.define('variant-group-price', VariantGroupPrice);
}
